import { useEffect, useRef, type FormEvent, type ReactNode } from 'react';

/**
 * Modal dialog on the native <dialog> element: focus is trapped, Esc closes,
 * and the page behind is inert. Render it conditionally; it opens on mount.
 */
export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
  onSubmit,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** When set the body is a form; Enter submits. */
  onSubmit?: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (!d.open) d.showModal();
    // showModal focuses the close button; start on the first field instead.
    const first = d.querySelector<HTMLElement>('.modal-body textarea, .modal-body input:not([type=hidden]):not([disabled]), .modal-body select:not([disabled])');
    first?.focus();
    const onCancel = (e: Event) => {
      e.preventDefault();
      closeRef.current();
    };
    d.addEventListener('cancel', onCancel);
    return () => {
      d.removeEventListener('cancel', onCancel);
      if (d.open) d.close();
    };
  }, []);

  const inner = (
    <>
      <div className="modal-head">
        <h2>{title}</h2>
        <button type="button" className="btn ghost sm" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      <div className="modal-body">{children}</div>
      {footer && <div className="modal-foot">{footer}</div>}
    </>
  );

  return (
    <dialog ref={ref} className={`modal ${wide ? 'wide' : ''}`} aria-modal="true">
      {onSubmit ? (
        <form
          className="modal-inner"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          {inner}
        </form>
      ) : (
        <div className="modal-inner">{inner}</div>
      )}
    </dialog>
  );
}
